import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, filter, map, Observable, tap } from 'rxjs';
import { ActivityCodeEnum, FormWizardSteps, PurposeEnum } from 'src/app/models/shared-models';
import { IRouteProperties, IRouteState, IWizardDataState } from '../models/form-wizard-models';
import { Logger } from './logger-service';
import { UtilService } from './util.service';
import { VegWizardDataService } from './veg-wizard-data.service';
import { environment } from "src/environments/environment";
import {FeatureFlags, FeatureFlagService } from 'src/app/services/feature-flag.service';

/**
* ------------------------------------------------------------------------------------------------------
* Veg Wizard - Side Menu State Machine Service
* ------------------------------------------------------------------------------------------------------
* This state machine will provide next and previous steps in the wizard process as well as provide
* information on which steps have been completed, which steps are hidden and which steps are visible but
* cannot be activiated due to dependencies not being met.
*/

@Injectable({
  providedIn: 'root'
})
@UntilDestroy()
export class VegWizardStateMachineService {

  // default route states, this is the first state.
  get defaultRouteStates() {
    return [{
      name: 'Pre-notification advice',
      route: FormWizardSteps.Home,
      isVisible: true,
      isEnabled: true,
      isActive: true
    },
    {
      name: 'Contact details',
      route: FormWizardSteps.LandHolderConfirmation,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Landholder details',
      route: FormWizardSteps.LandHolderDetails,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Notifier details',
      route: FormWizardSteps.NotifierDetails,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Lot on plan',
      route: FormWizardSteps.LotOnPlan,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Select purpose',
      route: FormWizardSteps.SelectPurpose,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Select category',
      route: FormWizardSteps.SelectCategory,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Self Audit',
      route: FormWizardSteps.SelfAudit,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Select activity',
      route: FormWizardSteps.SelectActivity,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Select regional ecosystem',
      route: FormWizardSteps.SelectRegionalEcosystem,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Select regional ecosystem status',
      route: FormWizardSteps.SelectRegionalEcosystemStatus,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Reason',
      route: FormWizardSteps.Reason,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Channel Dimensions',
      route: FormWizardSteps.ChannelDimensions,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Notification area',
      route: FormWizardSteps.NotificationArea,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Exchange Area',
      route: FormWizardSteps.ExchangeArea,
      isVisible: false,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Acknowledgements',
      route: FormWizardSteps.Acknowledgements,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Review notification',
      route: FormWizardSteps.ReviewNotification,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Declarations',
      route: FormWizardSteps.Declarations,
      isVisible: true,
      isEnabled: false,
      isActive: false
    },
    {
      name: 'Acknowledge notification',
      route: FormWizardSteps.AcknowledgeNotification,
      isVisible: true,
      isEnabled: false,
      isActive: false
    }] as IRouteState[];
  }

  // obervable which will update as the user navigates through the wizard
  // based on the data and choices they have completed.
  routeStates$: BehaviorSubject<IRouteState[]> = new BehaviorSubject<IRouteState[]>([]);
  routeStatesVisible$: Observable<IRouteState[]>;

  // the current route
  route = 'home';

  constructor(private router: Router,
    private vwds: VegWizardDataService,
    private utilService: UtilService,
    private logger: Logger,
    private featureFlagService: FeatureFlagService,
  ) {

    // subscribe to user navigation events, and update our state based on the
    // current route and wizard data state.
    router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationStart),
        tap((event) => {
          this.route = this.getRoute((event as NavigationStart).url) ?? 'home';
          const dataState = this.vwds.getWizardDataState();
          this.setRoutes(this.route, dataState);
        })
      )
      .subscribe();

    this.routeStatesVisible$ = this.routeStates$.pipe(map(s => s.filter(rs => rs.isVisible)));

  }

  /**
  * Get the next route
  * @returns the route to the next step
  */
  getNext$(): Observable<string> {

    // recalculate the data states during the next button request
    const dataState = this.vwds.getWizardDataState();
    this.setRoutes(this.route, dataState);
    this.routeStatesVisible$ = this.routeStates$.pipe(map(s => s.filter(rs => rs.isVisible)));

    return this.routeStates$
      .pipe(
        map(rs => {
          const currentIndex = rs.map(s => s.route).indexOf(this.route);

          // find the next route from the current index (index is order) that is visible and enabled.
          let nextIndex = currentIndex + 1;
          for (let i = (currentIndex + 1); i < rs.length; i++) {
            if (!rs[i].isEnabled || !rs[i].isVisible) { continue; }
            nextIndex = i;
            break;
          }

          return rs[nextIndex]?.route;
        }));
  }

  /**
  * Get the previous route
  * @returns the route to the prev step
  */
  getPrev$(): Observable<string> {

    // recalculate the data states during the previous button request
    const dataState = this.vwds.getWizardDataState();
    this.setRoutes(this.route, dataState);
    this.routeStatesVisible$ = this.routeStates$.pipe(map(s => s.filter(rs => rs.isVisible)));

    return this.routeStates$
      .pipe(
        map(rs => {
          const currentIndex = rs.map(s => s.route).indexOf(this.route);
          // find the prev route from the current index (index is order) that is visible and enabled.
          let prevIndex = currentIndex + 1;
          for (let i = (currentIndex - 1); i >= 0; i--) {
            if (!rs[i].isEnabled || !rs[i].isVisible) { continue; }
            prevIndex = i;
            break;
          }
          return rs[prevIndex]?.route;
        }));
  }

  private setRoutes(route: string, dataState: IWizardDataState) {
    const dfrs = this.utilService.clone(this.defaultRouteStates) as IRouteState[];
    const currentRoutePageIndex = this.defaultRouteStates.map(x => x.route).indexOf(this.route);
    let foundTheNextEnabledAndVisiblePage = false;

    dfrs.forEach((s, index) => {
      // determine if this is the route the user is currenting viewing.
      s.isActive = this.utilService.isEqual(s.route, route);
      if (s.isActive) {
        // if so, make sure it is enabled and visible.
        s.isEnabled = true;
        s.isVisible = true;
      } else {
        // if we're not on this route, set its properties
        // based on data the user may have already completed.
        const rp = this.getRouteProperties(s.route, dataState);
        if (rp) {
          s.isEnabled = rp.isEnabled;
          s.isVisible = rp.isVisible;
        }

        // users cannot access pages that are further ahead (except the next one)
        if (index > currentRoutePageIndex) {
          if (s.isEnabled && s.isVisible && !foundTheNextEnabledAndVisiblePage) {
            foundTheNextEnabledAndVisiblePage = true;
          }
          else {
            s.isEnabled = false;
          }
        }
      }
    });

    this.routeStates$.next(dfrs);
  }

  private getRouteProperties(route: string, dataState: IWizardDataState) {
    switch (route) {
      case FormWizardSteps.LandHolderConfirmation:
        return this.getLandholderConfirmation(dataState);

      case FormWizardSteps.LandHolderDetails:
        return this.getLandHolderDetailsRouterProperties(dataState);

      case FormWizardSteps.NotifierDetails:
        return this.getNotifierDetailsRouterProperties(dataState);

      case FormWizardSteps.LotOnPlan:
        return this.getLotPlanRouteProperties(dataState);

      case FormWizardSteps.SelectPurpose:
        return this.getSelectPurposeRouterProperties(dataState);

      case FormWizardSteps.SelectCategory:
        return this.getSelectCategoryRouteProperties(dataState);

      case FormWizardSteps.ExchangeArea:
        return this.getExchangeAreaRouteProperties(dataState);

      case FormWizardSteps.SelfAudit:
        return this.getSelfAuditRouteProperties(dataState);

      case FormWizardSteps.SelectRegionalEcosystemStatus:
        return this.getSelectRegionalEcosystemStatusRouteProperties(dataState);

      case FormWizardSteps.SelectRegionalEcosystem:
        return this.getSelectRegionalEcosystemRouteProperties(dataState);

      case FormWizardSteps.Reason:
        return this.getSelectReasonsRouteProperties(dataState);

      case FormWizardSteps.ChannelDimensions:
        return this.getSelectChannelDimensionsRouteProperties(dataState);

      case FormWizardSteps.SelectActivity:
        return this.getSelectActivityRouteProperties(dataState);

      case FormWizardSteps.NotificationArea:
        return this.getNotificationAreaRouteProperties(dataState);

      case FormWizardSteps.Acknowledgements:
        return this.getAcknowledgementRouteProperties(dataState);

      case FormWizardSteps.ReviewNotification:
        return this.getReviewNotificationRouteProperties(dataState);

      case FormWizardSteps.Declarations:
        return this.getDeclarationRouteProperties(dataState);

      case FormWizardSteps.AcknowledgeNotification:
        return this.getAcknowledgeNotificationRouteProperties(dataState);

      default:
        return undefined;
    }
  }

  public getLandholderConfirmation(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: true,
      isVisible: true
    };
  }

  public getLandHolderDetailsRouterProperties(dataState: IWizardDataState): IRouteProperties {
    const isNotifierDetailsRequired = !!this.vwds.getLandholderConfirmationModel()?.areYouAuthorised;
    return {
      isEnabled: dataState.isLandHolderConfirmationPopulated || isNotifierDetailsRequired,
      isVisible: true
    };
  }

  public getNotifierDetailsRouterProperties(dataState: IWizardDataState): IRouteProperties {
    const isNotifierDetailsRequired = !!this.vwds.getLandholderConfirmationModel()?.areYouAuthorised;
    return {
      isEnabled: dataState.isLandHolderDetailsPopulated || isNotifierDetailsRequired,
      isVisible: isNotifierDetailsRequired
    }
  }

  public getLotPlanRouteProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: dataState.isLandHolderDetailsPopulated,
      isVisible: true
    };
  }

  public getSelectPurposeRouterProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: dataState.isLotPlanPopulated,
      isVisible: true
    };
  }

  public getSelectCategoryRouteProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: dataState.isPurposePopulated,
      isVisible: true
    };
  }

  public getSelectRegionalEcosystemRouteProperties(dataState: IWizardDataState): IRouteProperties {

    return {
      isEnabled: this.showRegionalEcosystems(dataState) &&
        (this.showActivities(dataState) && dataState.isActivityPopulated
          || !this.showActivities(dataState) && dataState.isPurposePopulated
        ),
      isVisible: this.showRegionalEcosystems(dataState),
    };
  }

  public getSelectRegionalEcosystemStatusRouteProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: this.showRegionalEcosystemStatuses(dataState) && (
        (this.showActivities(dataState) && dataState.isActivityPopulated)
        || (!this.showActivities(dataState) && dataState.isPurposePopulated)),
      isVisible: this.showRegionalEcosystemStatuses(dataState)
    };
  }

  public getSelectReasonsRouteProperties(dataState: IWizardDataState): IRouteProperties {

    return {
      isEnabled: this.showReason(dataState) && dataState.isPurposePopulated,
      isVisible: this.showReason(dataState),
    }
  }

  public getSelectChannelDimensionsRouteProperties(dataState: IWizardDataState): IRouteProperties {
    const isSelectChannelDimensionsRequired = this.vwds.getActivities()?.selectedActivities?.some(act => act.requiresChannelDimensions) ?? false;
    return {
      isEnabled: dataState.isCategoryPurposePopulated && isSelectChannelDimensionsRequired,
      isVisible: dataState.isCategoryPurposePopulated && isSelectChannelDimensionsRequired,
    }
  }

  public getSelectActivityRouteProperties(dataState: IWizardDataState): IRouteProperties {
    const isSelectActivityRequired = this.vwds.getCategories()?.some(category => category.requiresActivities);
    return {
      isEnabled: dataState.isCategoryPurposePopulated && isSelectActivityRequired,
      isVisible: dataState.isCategoryPurposePopulated && isSelectActivityRequired,
    };
  }

  public getExchangeAreaRouteProperties(dataState: IWizardDataState): IRouteProperties {
    const isExchangeAreaRequired = this.vwds.getIsExchangeAreaRequired();
    return {
      isEnabled: dataState.isCategoryPurposePopulated && isExchangeAreaRequired,
      isVisible: dataState.isCategoryPurposePopulated && isExchangeAreaRequired,
    };
  }

  public getSelfAuditRouteProperties(dataState: IWizardDataState): IRouteProperties {
    const isSelfAuditRequired = this.vwds.getSelfAuditRequired();
    return {
      isEnabled: dataState.isCategoryPurposePopulated && isSelfAuditRequired,
      isVisible: dataState.isCategoryPurposePopulated && isSelfAuditRequired,
    };
  }


  public getNotificationAreaRouteProperties(dataState: IWizardDataState): IRouteProperties {

    const notificationAreaEnabled = (this.showRegionalEcosystems(dataState) && dataState.isRegionalEcosystemPopulated
      || !this.showRegionalEcosystems(dataState) && this.showActivities(dataState) && dataState.isActivityPopulated
      || !this.showActivities(dataState) && !this.showRegionalEcosystems(dataState) && dataState.isPurposePopulated
    ) && this.showNotificationArea(dataState)

    return {
      isEnabled: notificationAreaEnabled,
      isVisible: dataState.isCategoryPurposePopulated && this.showNotificationArea(dataState),
    };
  }

  public getAcknowledgementRouteProperties(dataState: IWizardDataState): IRouteProperties {

    let notificationAreaREdependency = false;

    if (this.showRegionalEcosystems(dataState)) {
      notificationAreaREdependency =  dataState.isRegionalEcosystemPopulated && dataState.isNotificationAreaPopulated;
    } else {
      notificationAreaREdependency = true;
    }

    return {
      isEnabled:
        ((dataState.isPurposePopulated && dataState.isCategoryPurposePopulated) && notificationAreaREdependency &&
        // ensure at least one datastate conditions are met - there are various conditions that allows this screen to appear!
        (dataState.isActivityPopulated || dataState.isExchangeAreaPopulated || dataState.isReasonPopulated ||
          dataState.isNotificationAreaPopulated || dataState.isChannelDimensionsPopulated))
           ,
      isVisible: true,
    };

  }

  public getReviewNotificationRouteProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: dataState.isAcknowledgementPopulated,
      isVisible: true,
    };
  }

  public getDeclarationRouteProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: dataState.isAcknowledgementPopulated,
      isVisible: true,
    };
  }

  public getAcknowledgeNotificationRouteProperties(dataState: IWizardDataState): IRouteProperties {
    return {
      isEnabled: dataState.isDeclarationPopulated,
      isVisible: true,
    };
  }

  private showRegionalEcosystems(dataState: IWizardDataState) {
    if (dataState.isCategoryPurposePopulated) {
      const activitiesDetails = this.vwds.getActivities();
      const selectedActivities = activitiesDetails ? activitiesDetails.selectedActivities : null;
      const categoriesModel = this.vwds.getCategories();

      if(this.featureFlagService.featureEnabled(FeatureFlags.NFP2019)) {
        // Special case for native forest practice and speciality timber harvesting
        if (this.vwds.getPurpose()?.purpose.purposeCode === PurposeEnum.nativeForestPractice &&
          this.vwds.getActivities()?.selectedActivities?.some(act => act.activityCode === ActivityCodeEnum.specialityTimberHarvesting)) {
          return true;
        }
      }

      if (selectedActivities !== null) {
        if (selectedActivities.some((activity) => activity.areRegionalEcosystemsRequired)) {
          return true;
        } else if (selectedActivities.length > 0 &&
          selectedActivities.every((activity) => activity.areRegionalEcosystemsRequired == false)) {
          return false;
        }
      }

      if (categoriesModel !== null) {
        return this.vwds.getCategories().some(category => category.areRegionalEcosystemsRequired)
          && !this.vwds.getActivities()?.selectedActivities?.some(act => act.activityCode === ActivityCodeEnum.smallScaleHarvesting);
      }
    }
    return false;
  }

  private showRegionalEcosystemStatuses(dataState: IWizardDataState) {

    if (dataState.isCategoryPurposePopulated) {
      return this.vwds.getCategories().some(category => category.usesRegionalEcosystemStatus)
        && !this.vwds.getActivities()?.selectedActivities?.some(act => act.activityCode === ActivityCodeEnum.smallScaleHarvesting);
    }
    return false;
  }

  private showReason(dataState: IWizardDataState) {
    if (dataState.isCategoryPurposePopulated) {
      const requiresReasonByCategoryPurpose = this.vwds.getCategories().some(category => category.requiresReason);
      if (dataState.isActivityPopulated) {
        const anySelectedActivitiesRequiresReason = this.vwds.getActivities()?.selectedActivities?.some(x => x.requiresReason);
        return anySelectedActivitiesRequiresReason ?? false;
      }
      else {
        return requiresReasonByCategoryPurpose;
      }
    }
    return false;
  }

  private showActivities(dataState: IWizardDataState) {
    if (dataState.isCategoryPurposePopulated) {
      return this.vwds.getCategories().some(category => category.requiresActivities)
    }
    return false;
  }

  private showNotificationArea(dataState: IWizardDataState) {
    if (dataState.isActivityPopulated && this.vwds.getActivities()?.selectedActivities != null) {
      return this.vwds.getActivities()?.selectedActivities?.some(x => x.requiresNotificationArea) ?? false
    }
    return true;
  }

  private getRoute(url: string): string | undefined {
    return url?.trim() ? url.replace('/', '').trim() : undefined;
  }

  public determineRouteNavigationForGuard(): any[] {
    const currentRoute = this.router.url.replace('/', '');
    const routerIndex = this.defaultRouteStates.findIndex(o => o.route === currentRoute);
    const newRouterIndex = (routerIndex - 1 < 0) ? 0 : routerIndex;
    return [this.defaultRouteStates[newRouterIndex].route];
  }
}
